import React, { useState, useEffect } from 'react';
import { fetchOwnedComponentSkins, searchComponentSkins } from '../../services/skinApi';
import { getSkinsForComponent } from './ComponentSkinLoader';
import SkinPurchaseModal from '../SkinPurchaseModal';

const ComponentSkinSelector = ({ 
  componentType, 
  selectedSkin, 
  onSkinChange,
  showPurchaseButton = true,
  style = {}
}) => {
  // componentType을 안전한 문자열로 변환
  const safeComponentType = (() => {
    if (!componentType) return null;
    if (typeof componentType === 'string') return componentType;
    if (typeof componentType === 'object') {
      if (typeof componentType.id === 'string') return componentType.id;
      if (typeof componentType.name === 'string') return componentType.name;
      if (typeof componentType.displayName === 'string') return componentType.displayName;
      return 'COMPONENT';
    }
    return 'COMPONENT';
  })();
  const [availableSkins, setAvailableSkins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // 사용 가능한 스킨 목록 로드
  useEffect(() => {
    const loadAvailableSkins = async () => {
      if (!safeComponentType) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // 1. 내장 스킨 가져오기 (로컬에서 등록된 스킨들)
        const internalSkins = getSkinsForComponent(safeComponentType);
        
        // 2. 보유한 스킨 가져오기 (API에서 보유한 스킨들)
        const ownedSkins = await fetchOwnedComponentSkins(safeComponentType);
        
        // 3. 스킨 목록 통합 (기본 스킨 추가)
        const allSkins = [
          // 기본 스킨 추가
          {
            id: 'basic',
            name: '기본 스킨',
            type: 'internal',
            available: true
          },
          // 내장 스킨들
          ...internalSkins.map(skin => ({
            ...skin,
            type: 'internal',
            available: true
          })),
          // 보유한 스킨들 (모두 사용 가능)
          ...ownedSkins.map(skin => {
            return {
              id: skin.id,
              name: skin.name,
              description: skin.description,
              author: skin.author || 'Unknown',
              type: 'owned',
              available: true, // 보유한 스킨은 모두 사용 가능
              preview: skin.thumbnail
            };
          })
        ];
        
        setAvailableSkins(allSkins);
        
      } catch (err) {
        console.error(`${safeComponentType} 스킨 로드 오류:`, err);
        setError(err.message);
        
        // 오류 시 기본 스킨과 내장 스킨만 사용
        const internalSkins = getSkinsForComponent(safeComponentType);
        setAvailableSkins([
          {
            id: 'basic',
            name: '기본 스킨',
            type: 'internal',
            available: true
          },
          ...internalSkins.map(skin => ({
            ...skin,
            type: 'internal',
            available: true
          }))
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadAvailableSkins();
  }, [safeComponentType]);

  // 스킨 구매 모달 열기
  const handleOpenPurchaseModal = () => {
    setShowPurchaseModal(true);
  };

  // 스킨 구매 완료 후 목록 갱신
  const handlePurchaseComplete = async () => {
    setShowPurchaseModal(false);
    
    // 서버 권한 업데이트 대기 (1초)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // 스킨 목록 다시 로드
    try {
      const ownedSkins = await fetchOwnedComponentSkins(safeComponentType);

      setAvailableSkins(prevSkins => {
        const internalSkins = prevSkins.filter(skin => skin.type === 'internal');
        const updatedOwnedSkins = ownedSkins.map(skin => {
          return {
            id: skin.id,
            name: skin.name,
            description: skin.description,
            author: skin.author || 'Unknown',
            type: 'owned',
            available: true, // 보유한 스킨은 모두 사용 가능
            preview: skin.thumbnail
          };
        });
        
        // 기본 스킨이 없다면 추가
        const hasBasicSkin = internalSkins.some(skin => skin.id === 'basic');
        const basicSkin = hasBasicSkin ? [] : [{
          id: 'basic',
          name: '기본 스킨',
          type: 'internal',
          available: true
        }];
        
        const finalSkins = [...basicSkin, ...internalSkins, ...updatedOwnedSkins];
        return finalSkins;
      });
    } catch (err) {
      console.error('스킨 목록 갱신 오류:', err);
    }
  };

  // 스킨 변경 핸들러
  const handleSkinChange = (e) => {
    const skinId = e.target.value;
    onSkinChange(skinId);
  };


  if (loading) {
    return (
      <div style={{ ...style, padding: '8px', color: '#666' }}>
        스킨 목록 로딩 중...
      </div>
    );
  }

  return (
    <div style={{ ...style }}>
      {/* 스킨 선택 드롭다운 */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <select
          value={selectedSkin || 'basic'}
          onChange={handleSkinChange}
          style={{ 
            flex: 1,
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            background: 'white'
          }}
        >
          {availableSkins.map(skin => (
            <option 
              key={skin.id} 
              value={skin.id}
            >
              {skin.name} 
              {skin.author && skin.author !== 'Unknown' ? ` (by ${skin.author})` : ''}
            </option>
          ))}
        </select>
        
        {/* 스킨 구매 버튼 */}
        {showPurchaseButton && (
          <button
            type="button"
            onClick={handleOpenPurchaseModal}
            style={{
              padding: '4px 12px',
              border: '1px solid #007bff',
              borderRadius: '4px',
              background: '#007bff',
              color: 'white',
              cursor: 'pointer',
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}
          >
            스킨 마켓
          </button>
        )}
        
      </div>
      
      {/* 오류 메시지 */}
      {error && (
        <div style={{ 
          marginTop: '4px', 
          color: '#d32f2f', 
          fontSize: '12px' 
        }}>
          오류: {error}
        </div>
      )}
      
      {/* 선택된 스킨 정보 */}
      {selectedSkin && (
        <div style={{ 
          marginTop: '4px', 
          fontSize: '12px', 
          color: '#666' 
        }}>
          {(() => {
            const currentSkin = availableSkins.find(skin => skin.id === selectedSkin);
            if (!currentSkin) return null;
            
            return (
              <div>
                현재 스킨: {currentSkin.name}
                {currentSkin.type === 'external' && ' (외부 스킨)'}
                {currentSkin.description && (
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>
                    {currentSkin.description}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
      
      {/* 스킨 구매 모달 */}
      {showPurchaseModal && (
        <SkinPurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          onPurchaseComplete={handlePurchaseComplete}
          skinType="COMPONENT"
          componentType={safeComponentType}
        />
      )}
    </div>
  );
};

export default ComponentSkinSelector;